import {
  DatePicker,
  DateTimePicker,
} from "@mui/x-date-pickers";
import { wizardValidationStyles } from "../styleUtilities";
import RadioGroup from "../../components/shared/ui/radio-group/RadioGroup";
import Input from "../../components/shared/ui/input/Input";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SearchableSelect from "../../components/ui/searchable-select/SearchableSelect";
import WizInput from "../../components/shared/wizard/WizInput";
import { dateFormatError, dateTimeError } from "../../constants/messages";

const inputUtilities = (formFields, setFormFields, styles) => {
  const dateFormat = "MM/DD/YYYY";
  const timeFormat = "MM/DD/YYYY  - hh:mm a";
  const inputProperties = (field, width) => {
    return {
      label: field.label,
      width: width,
      required: field.required,
      forId: field.fieldName,
    };
  }; //helper to generate the Input properties for the different input helpers

  const displayError = (field) => {  
    return field.visited && field.valid === false ? (
      <p
        id={`${field.fieldName}Error`}
        className={`${styles.formError} ${field.errorMessage?.length > 100 ? styles.veryExtraLong : field.errorMessage?.length > 60 ? styles.extraLong : field.errorMessage?.length > 30 ? styles.long : ""}`}>
        {field.errorMessage}
      </p>
    ) : null;
  }; //helper to display error message when appropriate (used for all input types)

  const dateProperties = (field, hasTime = false) => {
    return {
      value: field.value,
      onChange: (value) => {
        handleFieldChange(value, field);
      },
      required: field.required,
      id: field.fieldName,
      error: false,
      helperText: null,
      ...field.additionalProps,
    };
  }; //helper for DatePicker and DateTimePicker properties

  const commonProperties = (field, readonly = false) => {
    return {
      id: field.fieldName,
      value: field.value,
      onChange: (event) => handleFieldChange(event.target.value, field, false),
      onBlur: (event) => handleFieldChange(event.target.value, field),
      required: field.required,
      ["aria-invalid"]: field.visited && field.valid === false,
      ["aria-errormessage"]: `${field.fieldName}Error`,
      readOnly: readonly,
    };
  };//helper to add properties common for inputs, textareas, etc.

  const selectProperties = (field, disabled = false) => {
    return {
      id: field.fieldName,
      value: field.value,
      onChange: (event) => handleFieldChange(event.target.value, field),
      ["aria-required"]: field.required,
      ["aria-invalid"]: field.visited && field.valid === false,
      ["aria-errormessage"]: `${field.fieldName}Error`,
      disabled: disabled,
    };
  };//belper to add properties common for selects

  const addClasses = (field, className, setRequired = false) => {
    return `${field.visited && field.valid === false ? styles.validationStyle : ""} ${setRequired && field.required ? styles.requiredField : ""} ${className}`;
  };

  const handleFieldChange = (
    value,
    field,
    validate = true,
    displayValue = null
  ) => {
    const fields = { ...formFields };
    field.value = value;
    field.displayValue = displayValue;
    if (validate || field.visited) {
      const result = field.validation(value);
      if (result === true || result === false) {
        field.valid = result;
      } else {
        field.errorMessage = result;
        field.valid = false;
      }
      field.visited = true;
    }
    setFormFields(fields);
  }; //sets field value and validates where appropriate

  const createInput = (
    field,
    width,
    type = "text",
    readonly = false,
    wiz = false,
    className,
    wizProps
  ) => {
    return (
      wiz ?
        <WizInput
          {...inputProperties(field, width)}
          {...wizProps}
          >
            <span
            style={{width: "100%", marginLeft: ".1rem", display: "flex", position: "relative"}}
            >
          <input
            className={addClasses(field, className)}
            type={type}
            {...commonProperties(field, readonly)}
          {...field.additionalProps}
        />
        {displayError(field)}
        </span>
          
        </WizInput>
      :
      <Input {...inputProperties(field, width)}>
        <input
          type={type}
          className={addClasses(field, className)}
          {...commonProperties(field, readonly)}
          {...field.additionalProps}
        />
        {displayError(field)}
      </Input>
    );
  }; //helper function to create any standard input (text, number, etc.) from a form field object

  const createWizInput = (
    field,
    width,
    className,
    wizProps = { punctuate: ":"},
    type = "text"
  ) => {
    return createInput(field, width, type, false, true, className, wizProps);
  };//helper to create text input from a form field object - for a wiz input to reduce parameters

  const createNoLabelInput = (field, width, className, type = "text") => {
    return <div className={styles.inputErrorWrapper} style={{width: `${width}`}}>
            <input
              aria-label={field.label}
              placeholder={field.label}
              className={addClasses(field, className, true)}
              {...commonProperties(field)}
              {...field.additionalProps}
              type={type}
              style={{width: "100%"}}
            />
            {displayError(field)}
            </div>;
  };


  const createCustomLabelInput = (field, width, className, labelClass, additionalProperties) => {
    const punctuate = 
    additionalProperties?.punctuate === "" || additionalProperties?.punctuate
    ? additionalProperties.punctuate
    : ":";
    const altLabel = additionalProperties?.altLabel || "";
    const type = additionalProperties?.type || "text";
    const placeholder = additionalProperties?.placeholder || field.label;
    return <>
            <label
              htmlFor={field.fieldName}
              className={`${field.required ? styles.requiredField : ""} ${labelClass}`}
            >
              {altLabel || field.label}{punctuate}
            </label>
            <div className={styles.inputErrorWrapper}>
      
              <input
                placeholder={placeholder}
                className={addClasses(field, className)}
                {...commonProperties(field)}
                {...field.additionalProps}
                type={type}
              />
            {displayError(field)}
    </div>
    </> ;
  };//creates an input with a custom label class

  const createEditScreenInput = (field, width, className, labelClass, additionalProperties) => {
    return createCustomLabelInput(field, width, className, labelClass, { placeholder: "--", ...additionalProperties });
  };//creates custom label input defaulting to a -- placeholder - helper for edit screens

  const createCheckbox = (field, width, horizontal = true) => {
    return (
      <Input {...inputProperties(field, width)} horizontal={horizontal}>
        <input
          type="checkbox"
          id={field.fieldName}
          value={field.value}
          onChange={(event) => {
            handleFieldChange(
              event.target.value === "false" ? true : false,
              field,
              false
            );
          }}
          className={`${styles.checkbox} ${field.value && styles.checked}`}
          required={field.required}
          aria-invalid={field.visited && field.valid === false}
          aria-errormessage={`${field.fieldName}Error`}
          {...field.additionalProps}
        />
        {displayError(field)}
      </Input>
    );
  };

  const createSecretInput = (field, width, type = "text") => {
    return (
      <Input {...inputProperties(field, width)}>
        <div style={{ position: "relative" }}>
          <input
            type={type}
            {...commonProperties(field)}
            {...field.additionalProps}
            className={field.masked && "masked"}
          />
          <button
            type="button"
            className="showHide"
            onClick={() => {
              const fields = { ...formFields };
              field.masked = !field.masked;
              setFormFields(fields);
            }}
            aria-label={`${field.masked ? "Show" : "Hide"} ${field.label}`}
          >
            <FontAwesomeIcon
              icon={field.masked ? faEyeSlash : faEye}
              size="1x"
            />
          </button>
          {displayError(field)}
        </div>
      </Input>
    );
  };

  const createTextArea = (
    field,
    width,
    wiz = false,
    className = "",
    wizProps = { punctuate: ":" }
  ) => {
    return wiz ?
     <WizInput {...inputProperties(field, width)} {...wizProps}>
        <textarea
          className={addClasses(field, className)}
          {...commonProperties(field)}
          {...field.additionalProps}
        ></textarea>
         {displayError(field)}
     </WizInput>
    : (
      <Input {...inputProperties(field, width)}>
        <textarea
          {...commonProperties(field)}
          {...field.additionalProps}
        ></textarea>
        {displayError(field)}
      </Input>
    );
  }; //helper function to create a textarea from a form field object

  const createCustomLabelTextArea = (field, width, className, labelClass, additionalProperties) => {
    const punctuate = 
    additionalProperties?.punctuate === "" || additionalProperties?.punctuate
    ? additionalProperties.punctuate
    : ":";
    const altLabel = additionalProperties?.altLabel || "";
    return <>
      <label
        htmlFor={field.fieldName}
        className={`${field.required ? styles.requiredField : ""} ${labelClass}`}
      >
        {altLabel || field.label}{punctuate}
      </label>
      <div className={styles.inputErrorWrapper}
      style={{width: "100%"}}
      >
        <textarea
        className={addClasses(field, className)}
        {...commonProperties(field)}
        {...field.additionalProps}
        style={{width: "100%"}}
        ></textarea>
        {displayError(field)}
      </div>
    </>;
  };

  const createDateInput = (field, width, dateOnly = false) => {
    return (
      <Input
        {...inputProperties(field, width)}
        label={
          dateOnly
            ? `${field.label} (${dateFormat})`
            : `${field.label} (MM/DD/YYYY - HH:MM am)`
        }
      >
          {dateOnly ? (
            <DatePicker
              format={dateFormat}
             {...dateProperties(field)}
            />
          ) : (
            <DateTimePicker
              format={timeFormat}
              {...dateProperties(field, true)}
            />
          )}
        {displayError(field)}
      </Input>
    );
  }; //helper function to create a date input from a form field object

  const createToggleInput = (field, width, options, anonStyle = false) => {
    return (
      <div style={{ width: width }} className={styles.responsiveWrapper}>
        <RadioGroup
          options={options}
          handleSelect={(option) => handleFieldChange(option.value, field)}
          selected={{ value: field.value }}
          groupName={field.fieldName}
          label={field.label}
          required={field.required}
          width="100%"
          anonStyle={anonStyle}
        ></RadioGroup>
        {displayError(field)}
      </div>
    );
  }; //helper function to create a toggle input from a form field object

  const createSelect = (
    field,
    width,
    options,
    readonly = false,
    includeBlank = true,
    horizontal = false,
    wiz = false,
    className = "",
    wizProps,
    placeholder
  ) => {
    return wiz ?
      <WizInput
        {...inputProperties(field, width)}
        {...wizProps}
      >
        <select
        {...selectProperties(field, readonly)}
          className={addClasses(field, className)}
        >
        {includeBlank ?
          <option value="" key=""></option>
        : placeholder ?
          <option value="" key="">{placeholder}</option>
        : null
        }
          {options.map((option) => (
            <option value={option} key={option}>
              {option}
            </option>
          ))}
          </select>
          {displayError(field)}
      </WizInput>
    : (
      <Input {...inputProperties(field, width)} horizontal={horizontal}>
        <select
        {...selectProperties(field, readonly)}
          className={addClasses(field, className)}
        >
          {includeBlank && <option value="" key=""></option>}
          {options.map((option) => (
            <option value={option} key={option}>
              {option}
            </option>
          ))}
        </select>
        {displayError(field)}
      </Input>
    );
  }; //helper function to create a select from a form field object

  const createWizSelect = (field, width, options, className, placeholder = "", wizProps = { punctuate: ":"}) => {
    return createSelect(field, width, options, false, placeholder === "", false, true, className, wizProps, placeholder);
  };

  const createNoLabelSelect = (
    field,
    width,
    options,
    className,
    placeholder = false
  ) => {
    return <div className={styles.inputErrorWrapper} style={{width: `${width}`}}>
            <select
              aria-label={field.label}
              placeholder={field.label}
              className={addClasses(field, className, true)}
              {...selectProperties(field)}
              width="100%"
            >
              {placeholder !== false && <option value="">{placeholder}</option>}
              {options.map((option) => (
              <option value={option} key={option}>
                {option}
              </option>
              ))}
            </select>
            {displayError(field)}
          </div>;
  };

  const createCustomLabelSelect = (
    field,
    options,
    className,
    labelClass,
    additionalProperties
  ) => {
    const punctuate = 
    additionalProperties?.punctuate === "" || additionalProperties?.punctuate
    ? additionalProperties.punctuate
    : ":";
    const altLabel = additionalProperties?.altLabel || "";
    const placeholder = additionalProperties?.placeholder || false;
    return <>
            <label
            htmlFor={field.fieldName}
            className={` ${field.required ? styles.requiredField : ""} ${labelClass}`}
            >
              {altLabel || field.label}{punctuate}
            </label>
            <div className={styles.inputErrorWrapper}>
            <select
              {...selectProperties(field)}
              className={addClasses(field, className)}
            >
          {placeholder !== false && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option value={option} key={option}>
              {option}
            </option>
          ))}
          </select>
      {displayError(field)}
    </div>
    </> ;
  };

  const createSearchableSelect = (
    field,
    width,
    options,
    includeBlank = true,
    placeholder = "",
    isObjArray = false,
    wiz = false
  ) => {
    let theOptions = [];
    if (includeBlank) {
      theOptions.push({ label: "", value: "" });
    }
    if (isObjArray) {
      theOptions = options;
    } else {
      options.forEach((option) =>
        theOptions.push({ label: option, value: option })
      );
    }
    const properties = {
      value: field.displayValue,
      onChange: (value) =>
        handleFieldChange(value?.value || "", field, true, value),
      options: theOptions,
      placeholder: placeholder,
      addProps: {
        id: field.fieldName,
        ["aria-required"]: field.required,
        ["aria-invalid"]: field.visited && field.valid === false,
        ["aria-erromessage"]: `${field.fieldName}Error`,
      },
      altColor: wiz ? "#000000" : null,
    };
    return (
      wiz ?
      <WizInput {...inputProperties(field, width)}>
        <div style={{width: width}}>
        <SearchableSelect {...properties}
        style={field.visited && field.valid === false
          ? wizardValidationStyles
          : {
              border: "1px solid grey",
              borderBottom: "1px solid grey",
            }
        }
        />
        </div>
      </WizInput>
      :
      <Input {...inputProperties(field, width)}>
        <SearchableSelect
        {...properties}
        />
        {displayError(field)}
      </Input>
    );
  };

  return {
    handleFieldChange,
    createInput,
    createWizInput,
    createNoLabelInput,
    createCustomLabelInput,
    createEditScreenInput,
    createSecretInput,
    createTextArea,
    createCustomLabelTextArea,
    createDateInput,
    createToggleInput,
    createSelect,
    createWizSelect,
    createNoLabelSelect,
    createCustomLabelSelect,
    createCheckbox,
    createSearchableSelect,
  };
};//creates a set of utilities for creating and validating inputs

export default inputUtilities;

//formFields is an object, the value of each key in it is a field object
//field objects have the following properties
//field : {
//  fieldName: string,//unique identifier for the field
//  label: string, //for display of the label
//  value: any,//for controlling the value of the input
//  required: boolean,//for controlling whether the input is required - optional for inputs that are not required
//  validation: function,//for validating the field
//  additionalProps: object,//optional - for adding additional html properties to the input
//  valid: boolean,//optional - can be created dynamically during validation
//  errorMessage: string,//optional - can be created dynamically during validation
//  visited: boolean,//optional - can be created dynamically during validation
//displayValue: for display of searchable selects - required for searchable selects
//}
